import { useCallback, useEffect, useState } from 'react';

import { createAdminApi, type AdminApi } from '@/admin/adminApi';
import { isForbidden, messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';

/**
 * Lecture d'une route d'administration, avec son propre cycle de vie.
 *
 * C'est ce qui fait que **chaque bloc du tableau de bord charge et echoue
 * seul** : une lecture par source, chacune avec son etat et son « Réessayer »,
 * jamais un `Promise.all` qui laisserait une route lente ou en panne priver
 * l'ecran de tout le reste.
 *
 * Quatre issues, jamais deux : attente, lecture reussie, **refus** (403 :
 * l'operateur est connecte, son role ne suffit pas — ce n'est pas une panne) et
 * erreur. Chaque issue porte l'**heure de la reponse** : l'ecran ne se relit
 * jamais seul (ADR 0035 §4), cette heure est le seul moyen de savoir si ce qu'on
 * lit est frais.
 *
 * `enabled` faux : rien n'est lu, l'etat reste « loading » (l'appelant ne rend alors pas le bloc).
 *
 * `refreshToken` est le bouton « Actualiser » de la page : le changer relit
 * toutes les sources d'un coup, sans remonter l'ecran. `read` doit etre stable
 * (`useCallback`) : c'est sa **valeur** qui decide d'une nouvelle lecture — une
 * periode change, une lecture repart ; rien d'autre n'en provoque.
 */
export type AdminRead<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: T; readonly at: Date }
  | { readonly kind: 'forbidden'; readonly at: Date }
  | { readonly kind: 'error'; readonly message: string; readonly at: Date };

type Settled<T> = Exclude<AdminRead<T>, { kind: 'loading' }>;

interface Outcome<T> {
  readonly read: (api: AdminApi) => Promise<T>;
  readonly attempt: number;
  readonly refreshToken: number;
  readonly result: Settled<T>;
}

export const useAdminRead = <T,>(
  read: (api: AdminApi) => Promise<T>,
  refreshToken: number,
  enabled = true,
): { readonly state: AdminRead<T>; readonly reload: () => void } => {
  const http = useHttpClient();
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<Outcome<T> | null>(null);

  useEffect(() => {
    // Une lecture inutile n'est pas gratuite : chacune ecrit une ligne `admin.access`.
    if (!enabled) {
      return;
    }

    let cancelled = false;

    // `try/catch` autour d'un `await` et non `.catch()` : un client qui echoue
    // de facon synchrone (double de test, URL invalide) serait sinon une erreur
    // de rendu au lieu d'un bloc en erreur.
    const load = async (): Promise<void> => {
      let result: Settled<T>;

      try {
        result = { kind: 'ready', data: await read(createAdminApi(http)), at: new Date() };
      } catch (error: unknown) {
        result = isForbidden(error)
          ? { kind: 'forbidden', at: new Date() }
          : { kind: 'error', message: messageOf(error), at: new Date() };
      }

      if (!cancelled) {
        setOutcome({ read, attempt, refreshToken, result });
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [http, read, attempt, refreshToken, enabled]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  // L'attente est **deduite** : tant que l'issue rangee ne porte pas la demande
  // en cours, on attend encore. Un `setState('loading')` pose dans l'effet
  // ajouterait un rendu a chaque lecture.
  const current = outcome !== null && outcome.read === read && outcome.attempt === attempt && outcome.refreshToken === refreshToken;

  return { state: current ? outcome.result : { kind: 'loading' }, reload };
};
